import { Fragment, useMemo, useRef, useState } from "react";
import Cleave from "cleave.js/react";
import { useLoaderData, useSearchParams, useSubmit } from "react-router-dom";

import ServerFooter from "src/components/common/ServerFooter";
import OutsideDivisionForm from "./OutsideDivisionForm";

type DivisionRow = {
    division: string;
    region: string;
    role: string;
    supervisorEmail: string;
    supervisorName: string;
    supervisorPhone: string;
};

type LoaderData = {
    divisions: DivisionRow[];
};

type ActiveCell = {
    division: string;
    key: keyof DivisionRow;
    value: string;
} | null;

const fields: (keyof DivisionRow)[] = ["division", "supervisorName", "supervisorEmail", "role", "supervisorPhone", "region"];

export default function OutsideDivision() {
    const { divisions } = useLoaderData() as LoaderData;
    const submit = useSubmit();
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showForm, setShowForm] = useState(false);
    const [active, setActive] = useState<ActiveCell>(null);
    const page = Number(searchParams.get("page") ?? 1);
    const limit = Number(searchParams.get("limit") ?? 10);
    const query = (searchParams.get("q") ?? "").toLowerCase();

    const filteredDivisions = useMemo(
        () => divisions.filter((division) => query.split(" ").every((term) => JSON.stringify(Object.values(division)).toLowerCase().includes(term))),
        [divisions, query]
    );

    const currentDivisions = useMemo(
        () => filteredDivisions.slice((page - 1) * limit, page * limit),
        [filteredDivisions, page, limit]
    );

    const updateQuery = (value: string) => {
        const params = new URLSearchParams(searchParams);
        params.set("q", value);
        params.set("page", "1");
        setSearchParams(params);
    };

    const submitUpdate = async (key: keyof DivisionRow, nextValue: string, original: DivisionRow) => {
        if (original[key] === nextValue) {
            setActive(null);
            return;
        }
        const formData = new FormData();
        formData.append("intent", "update");
        formData.append("division", original.division);
        formData.append("key", key);
        formData.append("value", nextValue);
        submit(formData, { method: "post" });
        setActive(null);
    };

    const renderCellEditor = (row: DivisionRow, key: keyof DivisionRow) => {
        if (!active || active.division !== row.division || active.key !== key) return row[key];

        if (key === "supervisorPhone") {
            return (
                <Cleave
                    pattern = "[0-9]{3}-[0-9]{3}-[0-9]{4}|$^"
                    className = "form-control flex-grow-1"
                    options = {{
                        blocks: [3, 3, 4],
                        delimiter: "-",
                        numericOnly: true,
                    }}
                    value = {active.value}
                    onChange = {e => {
                        setActive({ ...active, value: e.currentTarget.value });
                    }}
                    onBlur = {e => {
                        if (!e.currentTarget.checkValidity()) {
                            window.alert("Input not valid.");
                            setActive(null);
                            return;
                        }
                        void submitUpdate(key, active.value, row);
                    }}
                />
            );
        }

        return (
            <textarea
                ref = {inputRef}
                className = "form-control"
                value = {active.value}
                onFocus = {e => {
                    const target = e.currentTarget;
                    target.selectionStart = target.selectionEnd = target.value.length;
                }}
                onChange = {e => {
                    setActive({ ...active, value: e.currentTarget.value });
                }}
                onBlur = {() => {
                    let value = active.value;
                    if (value !== "N/A") {
                        if (key === "supervisorEmail") {
                            value = value.replace(/ /g, "").toUpperCase();
                        } else {
                            value = value.replace(/\n/g, " ").replace(/\s+/g, " ").trim();
                            const properCaseValue = value.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
                            if (value !== properCaseValue && window.confirm(`Do you want to change your input from "${value}" to "${properCaseValue}"?\n(correct to proper case)`)) {
                                value = properCaseValue;
                            }
                        }
                    }
                    void submitUpdate(key, value, row);
                }}
            />
        );
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-end flex-wrap gap-3">
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "outsideDivisionSearch">Search</label>
                    <input
                        id = "outsideDivisionSearch"
                        className = "form-control w-auto"
                        type = "text"
                        value = {searchParams.get("q") ?? ""}
                        onChange = {e => updateQuery(e.currentTarget.value)}
                    />
                </div>
                <button type = "button" className = "btn btn-primary" onClick = {() => setShowForm(true)}>Add</button>
            </nav>
            {showForm ? <OutsideDivisionForm closeIt = {() => setShowForm(false)} /> : null}
            {!currentDivisions.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <>
                    <div className = "table-responsive">
                        <table className = "table">
                            <thead className = "noTableTopBorder">
                                <tr>
                                    <th>Division</th>
                                    <th>Supervisor Name</th>
                                    <th>Supervisor Email</th>
                                    <th>Role</th>
                                    <th>Supervisor Phone Number</th>
                                    <th>Region</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentDivisions.map((row) => (
                                    <Fragment key = {row.division}>
                                        <tr
                                            onDoubleClick = {e => {
                                                const target = e.target as HTMLElement;
                                                const key = target.id as keyof DivisionRow;
                                                if (!fields.includes(key)) return;
                                                setActive({ division: row.division, key, value: String(row[key] ?? "") });
                                            }}
                                        >
                                            {fields.map((key) => (
                                                <td key = {key} id = {key}>
                                                    {renderCellEditor(row, key)}
                                                </td>
                                            ))}
                                            <td>
                                                <button
                                                    type = "button"
                                                    className = "btn btn-danger"
                                                    onClick = {() => {
                                                        if (!window.confirm("Are you sure to delete this division?")) return;
                                                        const formData = new FormData();
                                                        formData.append("intent", "delete");
                                                        formData.append("division", row.division);
                                                        submit(formData, { method: "post" });
                                                    }}
                                                >
                                                    X
                                                </button>
                                            </td>
                                        </tr>
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <ServerFooter length = {filteredDivisions.length} />
                </>
            )}
        </div>
    );
}
